import React, { useState } from 'react';
import {
    Box,
    Avatar,
    Typography,
    Chip,
    IconButton,
    Collapse,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { MenteeMatchOverview } from '../../../../types/Matching';
import MentorMatchRow from './MentorMatchRow';

interface MenteePanelProps {
    mentee: MenteeMatchOverview;
    onViewDetails: (profile: any, mentorMatch: any) => void;
    onFinalizeMatch: (menteeId: string, mentorId: string) => void;
}

const MATCHED_COLOR = '#4caf50';

const MenteePanel: React.FC<MenteePanelProps> = ({ mentee, onViewDetails, onFinalizeMatch }) => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const toggleExpand = () => setExpanded(!expanded);

    const fullName = `${mentee.first_name} ${mentee.last_name}`;

    const matchableCount = mentee.possible_matches.filter(pm => pm.matchable).length;

    const matchedChip = mentee.matched ? (
        <Chip label="Matched" size="small" sx={{ ml: 1, bgcolor: MATCHED_COLOR, color: 'white' }} />
    ) : (
        <Chip label="Unmatched" color="default" size="small" sx={{ ml: 1 }} />
    );

    // Fake profile object matching MentorMatchRow expectation
    const menteeProfile = {
        id: fullName,
        name: fullName,
        avatar: `${mentee.first_name.charAt(0)}${mentee.last_name.charAt(0)}`,
        status: mentee.matched ? 'matched' : 'unmatched'
    };

    return (
        <Paper variant="outlined" sx={{ mb: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, cursor: 'pointer' }} onClick={toggleExpand}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>{menteeProfile.avatar}</Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {fullName} {matchedChip}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {matchableCount} matchable mentors
                    </Typography>
                </Box>
                <IconButton size="small" onClick={toggleExpand}>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Mentor</TableCell>
                            <TableCell>Score</TableCell>
                            <TableCell>Matchable</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mentee.possible_matches
                            .sort((a, b) => {
                                if (a.matchable && !b.matchable) return -1;
                                if (!a.matchable && b.matchable) return 1;
                                return b.score - a.score;
                            })
                            .map((match) => (
                                <MentorMatchRow
                                    key={`${fullName}-${match.first_name}-${match.last_name}`}
                                    mentorMatch={{ mentor: { ...match, name: `${match.first_name} ${match.last_name}`, avatar: `${match.first_name.charAt(0)}${match.last_name.charAt(0)}` }, overallScore: match.score, isMatchable: match.matchable }}
                                    profile={menteeProfile}
                                    onViewDetails={onViewDetails}
                                    onFinalizeMatch={onFinalizeMatch}
                                />
                            ))}
                    </TableBody>
                </Table>
            </Collapse>
        </Paper>
    );
};

export default MenteePanel;
